import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { Send, FileText, Eye, Copy, Check } from "lucide-react";

const TABS = ["stats", "quotes", "magazines", "articles", "appointments", "brochures"];

const STATUS_LABEL = {
  new: "Nouveau",
  contacted: "Contacté",
  questionnaire_sent: "Questionnaire envoyé",
  qualified: "Qualifié",
  proposal_sent: "Proposition envoyée",
  proposal_accepted: "Acceptée",
  proposal_declined: "Déclinée",
  closed: "Fermé",
};

function CopyButton({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}
      className="gai-btn-ghost text-xs"
      data-testid="copy-btn"
    >
      {done ? <Check size={12} /> : <Copy size={12} />} {done ? "Copié" : "Copier"}
    </button>
  );
}

function QuoteFunnelCard({ q, onChange }) {
  const [showAnswers, setShowAnswers] = useState(false);
  const [showProposal, setShowProposal] = useState(false);
  const [proposal, setProposal] = useState({
    amount: "", currency: "EUR", description: "",
    deliverables: "", validity_days: 30,
  });
  const [tokenUrl, setTokenUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  const sendQ = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/quotes/${q.id}/send-questionnaire`);
      setTokenUrl(data.url);
      onChange();
    } finally { setBusy(false); }
  };

  const sendProposal = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/quotes/${q.id}/proposal`, {
        amount: parseFloat(proposal.amount) || 0,
        currency: proposal.currency,
        description: proposal.description,
        deliverables: proposal.deliverables.split("\n").map((s) => s.trim()).filter(Boolean),
        validity_days: parseInt(proposal.validity_days, 10) || 30,
      });
      setTokenUrl(data.url);
      setShowProposal(false);
      onChange();
    } finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/admin/quotes/${q.id}`, { status });
      onChange();
    } finally { setBusy(false); }
  };

  // Compute the public link to share with the client
  const url = tokenUrl || (q.questionnaire_token
    ? (q.proposal
        ? `${window.location.origin}/devis/proposition/${q.questionnaire_token}`
        : `${window.location.origin}/devis/questionnaire/${q.questionnaire_token}`)
    : null);

  return (
    <div className="gai-card gai-sharp p-5" data-testid={`admin-quote-${q.id}`}>
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div className="min-w-0">
          <div className="font-medium text-lg">{q.full_name} {q.company ? `· ${q.company}` : ""}</div>
          <div className="text-xs text-[var(--gai-grey-500)] mt-1">
            {q.email} · {q.phone || "—"} · {q.country || "—"}
          </div>
          <div className="mt-2 text-sm">{q.service_type} · {q.budget_range}</div>
          <p className="mt-2 text-sm text-[var(--gai-grey-700)] max-w-2xl">{q.message}</p>
        </div>
        <div className="text-right shrink-0">
          <span className="overline text-[var(--gai-red)]">{STATUS_LABEL[q.status] || q.status}</span>
          <div className="text-[0.65rem] tracking-[0.18em] uppercase text-[var(--gai-grey-400)] mt-1">
            {new Date(q.created_at).toLocaleDateString("fr-FR")}
          </div>
        </div>
      </div>

      {/* Funnel actions */}
      <div className="mt-4 flex flex-wrap gap-2 pt-4 border-t border-[var(--gai-grey-200)]">
        {!q.questionnaire_token && (
          <button onClick={sendQ} disabled={busy} className="gai-btn-outline text-xs" data-testid={`admin-send-questionnaire-${q.id}`}>
            <Send size={12} /> Envoyer questionnaire
          </button>
        )}
        {q.questionnaire_answers && (
          <button onClick={() => setShowAnswers((s) => !s)} className="gai-btn-ghost text-xs" data-testid={`admin-view-answers-${q.id}`}>
            <Eye size={12} /> {showAnswers ? "Masquer" : "Voir"} les réponses
          </button>
        )}
        {q.questionnaire_answers && !q.proposal && (
          <button onClick={() => setShowProposal((s) => !s)} className="gai-btn-gold text-xs" data-testid={`admin-build-proposal-${q.id}`}>
            <FileText size={12} /> Générer proposition
          </button>
        )}
        <button onClick={() => setStatus("contacted")} className="gai-btn-ghost text-xs">Marquer contacté</button>
        <button onClick={() => setStatus("closed")} className="gai-btn-ghost text-xs">Fermer</button>
      </div>

      {url && (
        <div className="mt-4 p-3 border border-[var(--gai-grey-300)] flex items-center gap-3 flex-wrap" data-testid={`admin-token-url-${q.id}`}>
          <span className="overline text-[0.6rem]">Lien client</span>
          <code className="text-[0.7rem] flex-1 break-all text-[var(--gai-grey-800)]">{url}</code>
          <CopyButton text={url} />
        </div>
      )}

      {showAnswers && q.questionnaire_answers && (
        <div className="mt-4 p-4 bg-[rgba(5,16,36,0.5)] border border-[var(--gai-grey-200)]" data-testid={`admin-answers-${q.id}`}>
          <div className="overline mb-3">Réponses</div>
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {Object.entries(q.questionnaire_answers).map(([k, v]) => (
              <div key={k}>
                <dt className="text-[0.65rem] tracking-[0.18em] uppercase text-[var(--gai-red)]">{k}</dt>
                <dd className="text-[var(--gai-grey-800)] mt-1">{Array.isArray(v) ? v.join(", ") : String(v || "—")}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {showProposal && (
        <form onSubmit={sendProposal} className="mt-4 p-4 border border-[var(--gai-grey-300)] space-y-3" data-testid={`admin-proposal-form-${q.id}`}>
          <div className="overline mb-2">Nouvelle proposition</div>
          <div className="grid grid-cols-3 gap-3">
            <input type="number" required className="gai-input" placeholder="Montant" value={proposal.amount}
                   onChange={(e) => setProposal({ ...proposal, amount: e.target.value })} data-testid={`proposal-amount-${q.id}`} />
            <select className="gai-input appearance-none" value={proposal.currency}
                    onChange={(e) => setProposal({ ...proposal, currency: e.target.value })}>
              {["EUR", "USD", "XOF", "XAF", "CDF", "MAD", "GBP"].map((c) =>
                <option key={c} value={c} style={{ background: "var(--gai-white)", color: "var(--gai-black)" }}>{c}</option>)}
            </select>
            <input type="number" className="gai-input" placeholder="Validité (jours)" value={proposal.validity_days}
                   onChange={(e) => setProposal({ ...proposal, validity_days: e.target.value })} />
          </div>
          <textarea required rows={4} className="gai-input resize-none" placeholder="Description de la proposition" value={proposal.description}
                    onChange={(e) => setProposal({ ...proposal, description: e.target.value })} data-testid={`proposal-description-${q.id}`} />
          <textarea rows={4} className="gai-input resize-none" placeholder="Livrables (un par ligne)" value={proposal.deliverables}
                    onChange={(e) => setProposal({ ...proposal, deliverables: e.target.value })} />
          <button disabled={busy} className="gai-btn-gold text-xs" data-testid={`proposal-submit-${q.id}`}>
            Envoyer la proposition au client
          </button>
        </form>
      )}

      {q.proposal && (
        <div className="mt-4 p-4 border border-[var(--gai-grey-300)]" data-testid={`admin-proposal-summary-${q.id}`}>
          <div className="overline mb-2">Proposition envoyée</div>
          <div className="serif text-2xl gai-gradient-gold">
            {new Intl.NumberFormat("fr-FR", { style: "currency", currency: q.proposal.currency }).format(q.proposal.amount)}
          </div>
          <p className="mt-2 text-sm text-[var(--gai-grey-700)] whitespace-pre-line">{q.proposal.description}</p>
          {q.proposal_decision && (
            <div className="mt-3 text-xs">
              <span className="overline text-[0.6rem]" style={{ color: q.proposal_decision.decision === "accepted" ? "var(--gai-red)" : "#f87171" }}>
                {q.proposal_decision.decision === "accepted" ? "Acceptée" : "Déclinée"}
              </span>
              {q.proposal_decision.note && <p className="mt-1 text-[var(--gai-grey-600)]">« {q.proposal_decision.note} »</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const { t, tr } = useI18n();
  const nav = useNavigate();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [magazines, setMagazines] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [appts, setAppts] = useState([]);
  const [brochures, setBrochures] = useState([]);

  const refresh = async () => {
    const [s, a, m, q, ap, b] = await Promise.all([
      api.get("/admin/stats").then((r) => r.data).catch(() => null),
      api.get("/articles?limit=50").then((r) => r.data).catch(() => []),
      api.get("/magazines?limit=50").then((r) => r.data).catch(() => []),
      api.get("/admin/quotes").then((r) => r.data).catch(() => []),
      api.get("/admin/appointments").then((r) => r.data).catch(() => []),
      api.get("/brochures").then((r) => r.data).catch(() => []),
    ]);
    setStats(s); setArticles(a); setMagazines(m); setQuotes(q); setAppts(ap); setBrochures(b);
  };

  useEffect(() => {
    if (user === false) nav("/connexion");
    else if (user && user.role !== "admin") nav("/dashboard");
    else if (user) refresh();
  }, [user, nav]);

  const [arForm, setArForm] = useState({
    title_fr: "", title_en: "", excerpt_fr: "", excerpt_en: "",
    body_fr: "", body_en: "", category: "finance", cover_url: "",
    author: "Rédaction GAI", published: true, featured: false,
  });
  const [magForm, setMagForm] = useState({
    title_fr: "", title_en: "", edition: "", cover_url: "", pdf_url: "",
    description_fr: "", description_en: "", published: true, featured: false, year: 2026,
  });

  const createArticle = async (e) => {
    e.preventDefault();
    await api.post("/articles", {
      title: { fr: arForm.title_fr, en: arForm.title_en },
      excerpt: { fr: arForm.excerpt_fr, en: arForm.excerpt_en },
      body: { fr: arForm.body_fr, en: arForm.body_en },
      category: arForm.category, cover_url: arForm.cover_url || null,
      author: arForm.author, published: !!arForm.published, featured: !!arForm.featured,
    });
    setArForm({ ...arForm, title_fr: "", title_en: "", excerpt_fr: "", excerpt_en: "", body_fr: "", body_en: "" });
    await refresh();
  };

  const createMagazine = async (e) => {
    e.preventDefault();
    await api.post("/magazines", {
      title: { fr: magForm.title_fr, en: magForm.title_en },
      edition: magForm.edition,
      cover_url: magForm.cover_url || null,
      pdf_url: magForm.pdf_url || null,
      description: { fr: magForm.description_fr, en: magForm.description_en },
      published: !!magForm.published, featured: !!magForm.featured,
      year: Number(magForm.year) || 2026,
    });
    setMagForm({ ...magForm, title_fr: "", title_en: "", edition: "", cover_url: "", pdf_url: "", description_fr: "", description_en: "" });
    await refresh();
  };

  const uploadFile = async (file, purpose) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("purpose", purpose);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const delArticle = async (id) => { await api.delete(`/articles/${id}`); refresh(); };
  const delMagazine = async (id) => { await api.delete(`/magazines/${id}`); refresh(); };
  const patchAppt = async (id, status) => { await api.patch(`/admin/appointments/${id}`, { status }); refresh(); };

  if (!user || user.role !== "admin") return <div className="p-16 text-center">{t("common.loading")}</div>;

  return (
    <div className="max-w-[1320px] mx-auto px-6 lg:px-10 pt-14 pb-24" data-testid="page-admin">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="overline mb-2">{t("admin.title")}</div>
          <h1 className="serif text-4xl md:text-5xl">Console</h1>
        </div>
        <button onClick={async () => { await logout(); nav("/"); }} className="gai-btn-outline" data-testid="admin-logout">Déconnexion</button>
      </div>

      <div className="gai-hairline my-8" />

      <div className="flex flex-wrap gap-2 mb-10" data-testid="admin-tabs">
        {TABS.map((k) => (
          <button key={k} onClick={() => setTab(k)}
                  className={`px-4 py-2 text-[0.72rem] tracking-[0.2em] uppercase border ${
                    tab === k ? "bg-[var(--gai-red)] text-[var(--gai-white)] border-[var(--gai-red)]"
                              : "border-[var(--gai-grey-200)] hover:border-[var(--gai-red)]"}`}
                  data-testid={`admin-tab-${k}`}>{k}</button>
        ))}
      </div>

      {tab === "stats" && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-[1px] bg-[var(--gai-grey-200)]" data-testid="admin-stats">
          {Object.entries(stats).map(([k, v]) => (
            <div key={k} className="bg-[var(--gai-off-white)] p-6" data-testid={`stat-${k}`}>
              <div className="serif text-4xl gai-gradient-gold">{v}</div>
              <div className="mt-1 text-[0.7rem] tracking-[0.2em] uppercase text-[var(--gai-grey-600)]">{k.replace(/_/g, " ")}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "quotes" && (
        <div data-testid="admin-quotes-list">
          <h3 className="serif text-2xl mb-4">Tunnel commercial · {quotes.length} demandes</h3>
          <p className="text-sm text-[var(--gai-grey-600)] mb-6">
            Capture lead → Envoi questionnaire → Réponses qualifiées → Génération proposition → Acceptation client.
          </p>
          <div className="space-y-4">
            {quotes.map((q) => <QuoteFunnelCard key={q.id} q={q} onChange={refresh} />)}
          </div>
        </div>
      )}

      {tab === "articles" && (
        <div className="grid lg:grid-cols-2 gap-10" data-testid="admin-articles">
          <form onSubmit={createArticle} className="gai-card gai-sharp p-6 space-y-4" data-testid="admin-article-form">
            <h3 className="serif text-2xl">{t("admin.newArticle")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <input className="gai-input" placeholder="Titre FR" value={arForm.title_fr} onChange={(e) => setArForm({ ...arForm, title_fr: e.target.value })} required data-testid="article-title-fr" />
              <input className="gai-input" placeholder="Title EN" value={arForm.title_en} onChange={(e) => setArForm({ ...arForm, title_en: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <textarea className="gai-input resize-none" rows={2} placeholder="Chapeau FR" value={arForm.excerpt_fr} onChange={(e) => setArForm({ ...arForm, excerpt_fr: e.target.value })} />
              <textarea className="gai-input resize-none" rows={2} placeholder="Excerpt EN" value={arForm.excerpt_en} onChange={(e) => setArForm({ ...arForm, excerpt_en: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <textarea className="gai-input resize-none" rows={4} placeholder="Corps FR" value={arForm.body_fr} onChange={(e) => setArForm({ ...arForm, body_fr: e.target.value })} />
              <textarea className="gai-input resize-none" rows={4} placeholder="Body EN" value={arForm.body_en} onChange={(e) => setArForm({ ...arForm, body_en: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select className="gai-input" value={arForm.category} onChange={(e) => setArForm({ ...arForm, category: e.target.value })}>
                {["finance", "politique", "socio-culturel", "interview", "top"].map((c) =>
                  <option key={c} value={c} style={{ background: "var(--gai-white)", color: "var(--gai-black)" }}>{c}</option>)}
              </select>
              <input className="gai-input" placeholder="URL couverture" value={arForm.cover_url} onChange={(e) => setArForm({ ...arForm, cover_url: e.target.value })} />
            </div>
            <input type="file" accept="image/*" onChange={async (e) => {
              if (e.target.files?.[0]) {
                const r = await uploadFile(e.target.files[0], "cover");
                setArForm((f) => ({ ...f, cover_url: `${process.env.REACT_APP_BACKEND_URL}${r.url}` }));
              }
            }} className="text-sm" data-testid="article-upload-cover" />
            <input className="gai-input" placeholder="Auteur" value={arForm.author} onChange={(e) => setArForm({ ...arForm, author: e.target.value })} />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={arForm.featured} onChange={(e) => setArForm({ ...arForm, featured: e.target.checked })} /> Featured</label>
            <button className="gai-btn-gold" data-testid="article-submit">Publier</button>
          </form>
          <div>
            <h3 className="serif text-2xl mb-4">{t("admin.articles")} ({articles.length})</h3>
            <ul className="space-y-3">
              {articles.map((a) => (
                <li key={a.id} className="gai-card gai-sharp p-4 flex justify-between items-center" data-testid={`admin-article-${a.id}`}>
                  <div>
                    <div className="font-medium">{tr(a.title)}</div>
                    <div className="text-xs text-[var(--gai-grey-500)]">{a.category} · {a.author}</div>
                  </div>
                  <button onClick={() => delArticle(a.id)} className="gai-btn-ghost text-red-300 hover:text-red-200">✕</button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === "magazines" && (
        <div className="grid lg:grid-cols-2 gap-10" data-testid="admin-magazines">
          <form onSubmit={createMagazine} className="gai-card gai-sharp p-6 space-y-4" data-testid="admin-magazine-form">
            <h3 className="serif text-2xl">{t("admin.newMagazine")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <input className="gai-input" placeholder="Titre FR" value={magForm.title_fr} onChange={(e) => setMagForm({ ...magForm, title_fr: e.target.value })} required data-testid="mag-title-fr" />
              <input className="gai-input" placeholder="Title EN" value={magForm.title_en} onChange={(e) => setMagForm({ ...magForm, title_en: e.target.value })} />
            </div>
            <input className="gai-input" placeholder="Édition (N°42 — Juin 2026)" value={magForm.edition} onChange={(e) => setMagForm({ ...magForm, edition: e.target.value })} required data-testid="mag-edition" />
            <div className="grid grid-cols-2 gap-3">
              <textarea className="gai-input resize-none" rows={2} placeholder="Description FR" value={magForm.description_fr} onChange={(e) => setMagForm({ ...magForm, description_fr: e.target.value })} />
              <textarea className="gai-input resize-none" rows={2} placeholder="Description EN" value={magForm.description_en} onChange={(e) => setMagForm({ ...magForm, description_en: e.target.value })} />
            </div>
            <div className="space-y-3 border border-[var(--gai-grey-200)] p-4">
              <div className="overline">Couverture</div>
              <input className="gai-input" placeholder="URL couverture" value={magForm.cover_url} onChange={(e) => setMagForm({ ...magForm, cover_url: e.target.value })} />
              <input type="file" accept="image/*" onChange={async (e) => {
                if (e.target.files?.[0]) {
                  const r = await uploadFile(e.target.files[0], "cover");
                  setMagForm((f) => ({ ...f, cover_url: `${process.env.REACT_APP_BACKEND_URL}${r.url}` }));
                }
              }} className="text-sm" data-testid="mag-upload-cover" />
            </div>
            <div className="space-y-3 border border-[var(--gai-grey-200)] p-4">
              <div className="overline">PDF (feuilletable)</div>
              <input className="gai-input" placeholder="URL PDF" value={magForm.pdf_url} onChange={(e) => setMagForm({ ...magForm, pdf_url: e.target.value })} />
              <input type="file" accept="application/pdf" onChange={async (e) => {
                if (e.target.files?.[0]) {
                  const r = await uploadFile(e.target.files[0], "magazine");
                  setMagForm((f) => ({ ...f, pdf_url: `${process.env.REACT_APP_BACKEND_URL}${r.url}` }));
                }
              }} className="text-sm" data-testid="mag-upload-pdf" />
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={magForm.featured} onChange={(e) => setMagForm({ ...magForm, featured: e.target.checked })} /> Featured</label>
            <button className="gai-btn-gold" data-testid="mag-submit">Publier édition</button>
          </form>
          <div>
            <h3 className="serif text-2xl mb-4">{t("admin.magazines")} ({magazines.length})</h3>
            <ul className="space-y-3">
              {magazines.map((m) => (
                <li key={m.id} className="gai-card gai-sharp p-4 flex justify-between items-center gap-4" data-testid={`admin-mag-${m.id}`}>
                  <div className="flex items-center gap-3">
                    {m.cover_url && <img src={m.cover_url} alt="" className="w-14 h-20 object-cover" />}
                    <div>
                      <div className="font-medium">{tr(m.title)}</div>
                      <div className="text-xs text-[var(--gai-grey-500)]">{m.edition}</div>
                      {m.pdf_url && <div className="text-xs text-[var(--gai-red)]">PDF OK</div>}
                    </div>
                  </div>
                  <button onClick={() => delMagazine(m.id)} className="gai-btn-ghost text-red-300">✕</button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === "appointments" && (
        <div data-testid="admin-appts-list">
          <h3 className="serif text-2xl mb-4">{t("admin.appointments")} ({appts.length})</h3>
          <div className="space-y-3">
            {appts.map((a) => (
              <div key={a.id} className="gai-card gai-sharp p-5" data-testid={`admin-appt-${a.id}`}>
                <div className="flex justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-medium">{a.full_name} — {a.company || "—"}</div>
                    <div className="text-xs text-[var(--gai-grey-500)]">{a.email} · {a.phone || "—"}</div>
                    <div className="mt-2 text-sm">{a.topic} · {a.preferred_date}</div>
                    {a.message && <p className="mt-2 text-sm text-[var(--gai-grey-700)]">{a.message}</p>}
                  </div>
                  <div className="flex flex-col gap-2 text-xs">
                    <span className="overline text-[var(--gai-red)]">{a.status}</span>
                    <button onClick={() => patchAppt(a.id, "confirmed")} className="gai-btn-ghost">Confirmer</button>
                    <button onClick={() => patchAppt(a.id, "declined")} className="gai-btn-ghost">Refuser</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "brochures" && (
        <div data-testid="admin-brochures-list">
          <h3 className="serif text-2xl mb-4">{t("admin.brochures")} ({brochures.length})</h3>
          <div className="space-y-3">
            {brochures.map((b) => (
              <div key={b.id} className="gai-card gai-sharp p-5" data-testid={`admin-brochure-${b.id}`}>
                <div className="font-medium">{tr(b.title)}</div>
                <div className="text-xs text-[var(--gai-grey-500)] mt-1 break-all">{b.pdf_url || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
